/** Lightweight dependency-free line chart for the Storage Trend card
 * (spec §8). No charting library exists in this repo yet (audited
 * `package.json`); a single line + area over <=90 points does not
 * warrant adding one — an honest empty state renders when `points` is
 * empty (spec: a brand-new Workspace's Overview must never fake a flat
 * trend line, see `storage_summary_service.get_storage_trend`'s own
 * docstring). */
import { useTranslation } from 'react-i18next'

export interface TrendPoint {
  date: string
  value: number
}

export default function TrendLineChart({
  points,
  height = 220,
  color = 'var(--primary)',
  formatValue,
  formatDate,
}: {
  points: TrendPoint[]
  height?: number
  color?: string
  formatValue: (v: number) => string
  formatDate: (d: string) => string
}) {
  const { t } = useTranslation(['overview'])

  if (points.length === 0) {
    return (
      <div
        style={{
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--text-muted)',
          fontSize: 13,
        }}
      >
        {t('overview:panels.storageTrend.empty')}
      </div>
    )
  }

  const width = 640
  const padding = { top: 16, right: 16, bottom: 28, left: 8 }
  const innerWidth = width - padding.left - padding.right
  const innerHeight = height - padding.top - padding.bottom

  const values = points.map((p) => p.value)
  const maxValue = Math.max(...values, 1)
  const minValue = Math.min(...values, 0)
  const range = maxValue - minValue || 1

  const xStep = points.length > 1 ? innerWidth / (points.length - 1) : 0
  const coords = points.map((p, i) => ({
    x: padding.left + i * xStep,
    y: padding.top + innerHeight - ((p.value - minValue) / range) * innerHeight,
  }))

  const linePath = coords.map((c, i) => `${i === 0 ? 'M' : 'L'}${c.x.toFixed(1)},${c.y.toFixed(1)}`).join(' ')
  const areaPath = `${linePath} L${coords[coords.length - 1].x.toFixed(1)},${(padding.top + innerHeight).toFixed(1)} L${coords[0].x.toFixed(1)},${(padding.top + innerHeight).toFixed(1)} Z`

  const labelCount = Math.min(5, points.length)
  const labelIndices =
    labelCount <= 1
      ? [0]
      : Array.from({ length: labelCount }, (_, i) => Math.round((i * (points.length - 1)) / (labelCount - 1)))

  return (
    <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" role="img" aria-label={t('overview:panels.storageTrend.ariaLabel')}>
      <path d={areaPath} fill={color} opacity={0.08} />
      <path d={linePath} fill="none" stroke={color} strokeWidth={2} />
      {coords.length <= 31 &&
        coords.map((c, i) => <circle key={i} cx={c.x} cy={c.y} r={2.5} fill={color} />)}
      {labelIndices.map((idx) => (
        <text
          key={idx}
          x={coords[idx].x}
          y={height - 8}
          fontSize={11}
          fill="var(--text-muted)"
          textAnchor={idx === 0 ? 'start' : idx === points.length - 1 ? 'end' : 'middle'}
        >
          {formatDate(points[idx].date)}
        </text>
      ))}
      <title>{points.map((p) => `${formatDate(p.date)}: ${formatValue(p.value)}`).join('\n')}</title>
    </svg>
  )
}
